import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { CardNotification, Text } from "@foris/avocado-suite";
import { useParams } from "react-router-dom";
import { IParams } from "@models/IParams";
import { AppContext } from "../../context/EditSessionsContext";
import { Types } from "../../context/switchPage.reducer";
import { useGetLink } from "../../hooks/useGetLinks";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import useContextUrl from "@common/hooks/useContextUrl";
import Compare from "../../components/Compare/Compare";
import IncidenceTable from "../../components/Incidence/IncidenceTable";
import useSessionsCrudMutation from "../../hooks/useSessionsCrudMutation";
import SavedSectionsList from "../../components/saved-sections-list";
import ResultsEditHeader from "../../components/results-edit-header";
import Modal from "@components/modal";
import InstructorRemovalModal, {
  isInstructorRemovalModalVisible,
} from "@components/instructor-removal-modal";
import { ContextApp } from "@config/Context/contextApp";
import { useInstructorRemovalValidation } from "../../hooks/useInstructorRemovalValidation";
import {
  resetInstructorRemovalFlow,
  setInstructorRemovalFlowState,
  useInstructorRemovalFlowStore,
} from "@context/instructorRemovalFlow/useInstructorRemovalFlowStore";
import {
  useProgrammingEditStore,
  resetProgrammingEditStore,
} from "@context/useProgrammingEditStore";
import { emitEvent } from "@common/hooks/useListenEvent";
import { useProgrammingSessionsCrud } from "@services/collections/programming-edit/programmingEditServices";
import css from "./resultEdit.module.scss";

interface IResultsEdit {
  heightHeader: number;
}

const ResultsEdit: React.FC<IResultsEdit> = (props: IResultsEdit) => {
  const { heightHeader } = props;
  const history = useHistory();
  const { id, workspace, scenario, origin } = useContextUrl();
  const { scenario: scenarioId, origin: originId }: IParams = useParams();
  const { state, dispatch } = useContext(AppContext);
  const [dataLinks, getDataLinks] = useGetLink();
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState(false);
  const submissionInFlight = useRef(false);
  const appContext = useContext(ContextApp);

  // --- Programming mode ---
  const isProgrammingMode = useProgrammingEditStore(s => s.isProgrammingResultsActive);
  const programmingChangeset = useProgrammingEditStore(s => s.programmingChangeset);
  const { submitCrud: submitProgrammingCrud, loading: programmingSubmitting } =
    useProgrammingSessionsCrud();

  // --- Assignment mode ---
  const [resultSessionsCrud, submitSessionsCrud] = useSessionsCrudMutation({
    dryRun: false,
    skipValidations: true,
  });
  const removalStatus = useInstructorRemovalFlowStore(flow => flow.status);
  const removalReasons = useInstructorRemovalFlowStore(flow => flow.reasons);
  const removalContext = useInstructorRemovalFlowStore(flow => flow.context);
  const canCorrectInBundle =
    !!appContext?.user?.permissions?.update &&
    !!appContext?.user?.abilities?.can_edit_groups_in_bundle_view;
  const {
    enabled: instructorRemovalEnabled,
    prepareBeforeSave,
    verifySavedLeague,
    retryVerification,
  } = useInstructorRemovalValidation({
    context: {
      workspaceId: String(workspace),
      scenarioId: String(scenario),
      originId: String(origin),
      linkId: String(id),
    },
    canCorrectInBundle,
  });

  const { editorUrl, bundleUrl } = useNavigationUrl();
  const { t } = useTranslation();

  const savedSessions = state?.form?.savedSessions ?? [];
  const sessionsToDelete = useMemo(
    () => Object.values(state?.form?.sessionsToDelete ?? []),
    [state?.form?.sessionsToDelete],
  );

  const numberOfSavedSessions = savedSessions?.length ?? 0;
  const numberOfSessionsToCreate = state?.form?.sessionsToCreate?.length ?? 0;
  const numberOfSessionsToDelete = sessionsToDelete?.length ?? 0;

  const numberOfEditedSessions = useMemo(
    () =>
      [...(savedSessions ?? [])].filter(session => !session?.isNew && !session?.isCloned).length,
    [savedSessions],
  );

  const numberOfNewSessions = useMemo(
    () => [...(savedSessions ?? [])].filter(session => session?.isNew || session?.isCloned).length,
    [savedSessions],
  );
  const someEditionMade = Boolean(
    numberOfSessionsToDelete + numberOfSavedSessions + numberOfSessionsToCreate,
  );

  const selectedEdit = state?.result?.selectSession;
  const selectedCreate = state?.result?.selectCreateSession;
  const loading = isProgrammingMode ? programmingSubmitting : state?.page?.loading;
  const removalModalVisible = isInstructorRemovalModalVisible(removalStatus);

  // --- Submit ---
  const submitForm = async () => {
    if (isProgrammingMode) {
      if (!programmingChangeset) return;
      try {
        dispatch({ type: Types.SetLoading, payload: true });
        const result = await submitProgrammingCrud({
          scenarioId,
          originId,
          input: { ...programmingChangeset, dryRun: false, skipValidations: true },
        });

        if (result?.commited) {
          setConfirm(true);
        } else {
          setError(true);
        }

        dispatch({ type: Types.SetLoading, payload: false });
      } catch {
        setError(true);
        dispatch({ type: Types.SetLoading, payload: false });
      }
      return;
    }

    if (!loading && someEditionMade && !submissionInFlight.current) {
      submissionInFlight.current = true;
      dispatch({ type: Types.SetLoading, payload: true });

      if (!instructorRemovalEnabled) {
        await submitSessionsCrud();
        submissionInFlight.current = false;
        return;
      }

      const snapshotReady = await prepareBeforeSave();
      if (!snapshotReady) {
        submissionInFlight.current = false;
        dispatch({ type: Types.SetLoading, payload: false });
        return;
      }

      const response = await submitSessionsCrud();
      if (response?.commited !== true) {
        setInstructorRemovalFlowState({ status: "assignment-save-failed" });
        submissionInFlight.current = false;
        dispatch({ type: Types.SetLoading, payload: false });
        return;
      }

      const outcome = await verifySavedLeague();
      await getDataLinks(true);
      if (outcome && !outcome.reasons.length) setConfirm(true);

      submissionInFlight.current = false;
      dispatch({ type: Types.SetLoading, payload: false });
    }
  };

  const handleRetryVerification = async () => {
    if (submissionInFlight.current) return;

    submissionInFlight.current = true;
    dispatch({ type: Types.SetLoading, payload: true });
    const outcome = await retryVerification();
    await getDataLinks(true);
    if (outcome && !outcome.reasons.length) setConfirm(true);
    submissionInFlight.current = false;
    dispatch({ type: Types.SetLoading, payload: false });
  };

  const handleReturnToAssignment = () => {
    const needsReplacement = removalStatus === "needs-replacement";
    window.dispatchEvent(new Event("fetch-link-sessions-info"));

    if (needsReplacement) {
      setInstructorRemovalFlowState({
        status: "editing-assignment",
        beforeSnapshot: null,
        afterSnapshot: null,
        reasons: [],
        pendingReplacement: true,
      });
    } else {
      resetInstructorRemovalFlow();
    }
  };

  const handleGoToBundle = () => {
    if (!removalContext?.bundleId) return;
    setInstructorRemovalFlowState({ status: "correcting-bundle" });
    history.push(bundleUrl(removalContext.bundleId));
  };

  // Assignment mode: handle mutation response
  useEffect(() => {
    if (isProgrammingMode || instructorRemovalEnabled) return;

    const responseData = resultSessionsCrud?.data;
    if (someEditionMade) {
      const validateEdited = numberOfEditedSessions > 0;
      const validateCreate = numberOfNewSessions + numberOfSessionsToCreate > 0;
      const validateDeleted = numberOfSessionsToDelete > 0;

      const goToResultView =
        (validateEdited && !validateCreate && responseData?.commited) ||
        (!validateEdited && validateCreate && responseData) ||
        (validateEdited && validateCreate && responseData) ||
        validateDeleted;

      if (goToResultView && !dataLinks && Boolean(responseData)) {
        getDataLinks();
      }
    }
    if (resultSessionsCrud?.error) setError(true);
  }, [resultSessionsCrud]);

  // Assignment mode: navigate after links loaded
  useEffect(() => {
    if (isProgrammingMode || instructorRemovalEnabled) return;

    if (Boolean(dataLinks) && Boolean(state?.link?.info) && !confirm) {
      setConfirm(true);
      dispatch({ type: Types.SetLoading, payload: false });
    }
  }, [dataLinks]);

  if (loading) return null;

  return (
    <>
      {confirm && (
        <Modal
          typeState="confirm"
          title={t("assignation-edit.incidence-table.notifications.saved-changes.title")}
          show={confirm}
          textButtonPrincipal={t(
            "assignation-edit.incidence-table.notifications.saved-changes.principal-action",
          )}
          onClickPrincipal={() => {
            if (isProgrammingMode) {
              resetProgrammingEditStore();
            }

            if (history.length > 1) {
              history.goBack();
            } else {
              history.push(editorUrl("link", id), {
                currentSelectedWeek: state.link.selectedWeek,
              });
            }
          }}
          textButtonSecondary={t(
            "assignation-edit.incidence-table.notifications.saved-changes.secondary-action",
          )}
          onClickSecondary={() => {
            window.dispatchEvent(new Event("fetch-link-sessions-info"));

            if (isProgrammingMode) {
              resetProgrammingEditStore();
              emitEvent("on:navigate", { target: "assignment-tab" });
            }
          }}
        >
          <Text>
            {t("assignation-edit.incidence-table.notifications.saved-changes.description")}
          </Text>
        </Modal>
      )}

      {!isProgrammingMode && (
        <InstructorRemovalModal
          status={removalStatus}
          reasons={removalReasons}
          onGoToBundle={handleGoToBundle}
          onReturnToAssignment={handleReturnToAssignment}
          onAcknowledge={handleReturnToAssignment}
          onRetryBeforeSave={submitForm}
          onRetryVerification={handleRetryVerification}
          onCloseAfterSave={handleReturnToAssignment}
          onCancelBeforeSave={() => {
            resetInstructorRemovalFlow();
            dispatch({ type: Types.FormPage, payload: true });
          }}
        />
      )}

      {error && (
        <Modal
          typeState="error"
          title={t("assignation-edit.incidence-table.notifications.error-validations.title")}
          show={error}
          textButtonPrincipal={t(
            "assignation-edit.incidence-table.notifications.error-validations.principal-action",
          )}
          onClickPrincipal={() => window.location.reload()}
        >
          <Text>
            {t("assignation-edit.incidence-table.notifications.error-validations.description")}
          </Text>
        </Modal>
      )}

      <div className={css.resultEdit}>
        {!!(selectedEdit || selectedCreate || sessionsToDelete) && (
          <ResultsEditHeader onSubmit={submitForm} />
        )}

        {!confirm && !removalModalVisible && (
          <section className={cx(css.content, css[`content__height${heightHeader}`])}>
            <aside className={cx(css.aside)}>
              <SavedSectionsList />
            </aside>

            <section className={cx(css.results)}>
              {selectedEdit || selectedCreate || sessionsToDelete ? (
                <section className={css.results_content}>
                  <Compare />
                  <IncidenceTable />
                </section>
              ) : (
                <CardNotification
                  state="info"
                  title={t("assignation-edit.incidence-table.notifications.info-message.title")}
                  className={css.initCard}
                >
                  {t("assignation-edit.incidence-table.notifications.info-message.description")}
                </CardNotification>
              )}
            </section>
          </section>
        )}
      </div>
    </>
  );
};

export default ResultsEdit;
